import { template as _$template } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<div id=cesium-container style=width:100%;height:100%;position:absolute;top:0;left:0>`);
/**
 * WorldView - Cesium Provider
 *
 * SolidJS context provider that manages the Cesium Viewer lifecycle.
 * Creates viewer in onMount, destroys in onCleanup.
 * Loads Google 3D Tiles for the globe.
 */

import { createContext, createSignal, onMount, onCleanup } from "solid-js";
import { PROXY_BASE_URL, PROXY_ENDPOINTS } from "../config";
// Google 3D Tiles URL via proxy
const GOOGLE_3D_TILES_URL = `${PROXY_BASE_URL}/v1/3dtiles/root.json`;

// Default camera position: 15,000km altitude, centered on 0°N 0°E
const DEFAULT_CAMERA = {
  longitude: -95.3698,
  latitude: 29.7469,
  height: 3_000 // 2026-08-31: Houston TX. Was 0/0/15_000_000 (empty space).
};
export const CesiumContext = createContext();
/**
 * CesiumProvider - Wraps the app with Cesium viewer context
 *
 * Usage:
 * ```tsx
 * <CesiumProvider>
 *   <App />
 * </CesiumProvider>
 * ```
 */
export function CesiumProvider(props) {
  const [viewer, setViewer] = createSignal(null);
  const [ready, setReady] = createSignal(false);
  let containerRef;
  onMount(async () => {
    if (!containerRef) {
      console.error("[CesiumProvider] Container ref not found");
      return;
    }

    // Disable Cesium Ion - we're using Google 3D Tiles directly
    Cesium.Ion.defaultAccessToken = "";
    const opts = props.options ?? {};

    // Create the Cesium Viewer with optimized settings
    const cesiumViewer = new Cesium.Viewer(containerRef, {
      // Disable default UI elements we don't need
      baseLayerPicker: false,
      // 2026-08-31: THE ion 401. Viewer builds a DEFAULT base imagery layer from
      // ion asset 2 during construction. imageryLayers.removeAll() below runs
      // AFTER that, so the request has already fired and 401'd on the empty token.
      // baseLayer:false stops it being created at all.
      baseLayer: false,
      geocoder: false,
      homeButton: false,
      sceneModePicker: false,
      selectionIndicator: opts.selectionIndicator ?? false,
      timeline: false,
      animation: false,
      navigationHelpButton: false,
      fullscreenButton: false,
      vrButton: false,
      infoBox: opts.infoBox ?? false,
      // Disable terrain - Google 3D Tiles includes terrain
      // 2026-08-31 ROOT-CAUSE FIX: `terrain: undefined` means "use the Viewer
      // DEFAULT", and that default is Cesium ion World Terrain (ion asset 2).
      // With line 79 setting an EMPTY token, every load fired
      // GET api.cesium.com/v1/assets/2/endpoint?access_token= -> 401 INVALID_TOKEN
      // and the 3D-tile traversal then requested ZERO tiles (measured: 0 .glb,
      // 0 pending, at t+0/8/16/24s over Houston). That was the black screen.
      terrainProvider: new Cesium.EllipsoidTerrainProvider(),
      // Enable standard mouse/touch controls (CesiumJS defaults)
      scene3DOnly: true,
      // Performance settings
      requestRenderMode: false,
      maximumRenderTimeChange: Infinity,
      // Rendering quality
      // 2026-08-31 THERMAL (Brockmann on device: "phone is HOT").
      // msaaSamples 4 = 4x multisample antialiasing on EVERY pixel. On a DPR-3
      // iPhone the canvas is ~1320x2868 = 3.8M pixels, so 4xMSAA is ~15M samples
      // per frame, at 60fps, forever (requestRenderMode is false above). That is
      // the GPU pinned at full load continuously — the phone has nowhere to dump
      // the heat and the battery drains with it.
      // Desktop keeps 4. Phones drop to 1: on a 3x display the aliasing MSAA
      // fixes is already below what the eye resolves, so this costs almost
      // nothing visually and removes most of the per-pixel work.
      msaaSamples: opts.msaaSamples ?? (typeof window !== "undefined" && window.innerWidth <= 768 ? 1 : 4)
    });

    // 2026-08-31 THERMAL, part 2. The two biggest remaining levers on a phone:
    //   resolutionScale — render at 70% of native and let the display upscale.
    //     Cuts pixels by ~51%. On a 3x-DPR screen this is very hard to see and it
    //     roughly halves fragment-shader load, which is where the heat comes from.
    //   targetFrameRate — 60fps on a map that is usually STATIONARY is wasted
    //     work. 30fps halves the frame count; panning still reads as smooth.
    // Applied to phones only. Desktop is untouched.
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      cesiumViewer.resolutionScale = 0.7;
      cesiumViewer.targetFrameRate = 30;
      console.log("[CesiumProvider] mobile thermal profile: msaa=1 resolutionScale=0.7 targetFrameRate=30");
    }
    // (viewer construction closed at line 130 above)

    // Remove default imagery layers since Google 3D Tiles provides everything
    cesiumViewer.imageryLayers.removeAll();

    // Remove default credit display
    const creditContainer = cesiumViewer.cesiumWidget.creditContainer;
    creditContainer.style.display = "none";

    // 2026-08-31 (Brockmann: "the default should apply to user location").
    // ORDER OF TRUTH for the opening camera:
    //   1. NATIVE PUSH — the iOS app sends the operator's real CLLocation via
    //      window.atlasFocusEntity (ContentView.swift ~6458; producer is
    //      BlueForceTracker.shared.currentCoordinate). That is the authoritative
    //      source in the app and it OVERRIDES whatever we set here, because it
    //      arrives after load. Nothing below fights it.
    //   2. BROWSER GEOLOCATION — for the desktop/web case. Note the app case is
    //      already known to fail here: WKWebView does not hand navigator.geolocation
    //      to the page the way Safari does, so this resolves null on device. That is
    //      expected, not a bug, and is exactly why (1) exists.
    //   3. DEFAULT_CAMERA — last resort only, so a cold load is never Null Island
    //      (0,0 in the Gulf of Guinea) which reads to an operator as "not loading".
    //
    // We set (3) SYNCHRONOUSLY first so there is never an unpositioned frame, then
    // upgrade to (2) if the browser answers. (1) wins whenever it arrives.
    cesiumViewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(DEFAULT_CAMERA.longitude, DEFAULT_CAMERA.latitude, DEFAULT_CAMERA.height)
    });
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        if (cesiumViewer.isDestroyed()) return;
        const {
          latitude,
          longitude
        } = pos.coords;
        console.log(`[CesiumProvider] geolocation fix -> ${latitude.toFixed(4)},${longitude.toFixed(4)}`);
        cesiumViewer.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(longitude, latitude, DEFAULT_CAMERA.height),
          duration: 2.0
        });
      }, err => {
        // Expected in WKWebView. Never invent a coordinate — stay on DEFAULT_CAMERA
        // and let the native push correct us if the operator has a real fix.
        console.log(`[CesiumProvider] no browser geolocation (${err.code}) — native push owns location`);
      }, {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 300000
      });
    }

    // (superseded — kept below as the original single setView is now above)
    // (the DEFAULT_CAMERA setView below is the synchronous step 3 described above)
    cesiumViewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(DEFAULT_CAMERA.longitude, DEFAULT_CAMERA.latitude, DEFAULT_CAMERA.height)
    });

    // Configure scene settings
    const {
      scene
    } = cesiumViewer;
    scene.globe.show = false; // Hide default globe since we use 3D tiles
    scene.globe.depthTestAgainstTerrain = false;
    // b600 (2026-07-24) — THE ACTUAL FIX for "RENDERING HAS STOPPED".
    // The failing shader is _uczm_computeGroundAtmosphere — that is the GROUND
    // atmosphere (globe.showGroundAtmosphere), NOT skyAtmosphere. The 07-22 fix
    // disabled the SKY one and the operator correctly reported the error was
    // unchanged. showGroundAtmosphere defaults to TRUE and was never set here,
    // so Cesium kept compiling the exact shader that fails MSL compilation via
    // ANGLE on this iOS/WebKit build, killing the whole render context.
    // globe.show=false does NOT prevent that shader being built.
    scene.globe.showGroundAtmosphere = false;
    // b600 pass 2 — showGroundAtmosphere alone did NOT clear it (operator
    // re-confirmed the identical error). Read the engine instead of guessing:
    // in Cesium 1.139 the GROUND_ATMOSPHERE shader define is pushed COUPLED
    // with FOG (GlobeSurfaceShaderSet: jm(defines,"GROUND_ATMOSPHERE") ...
    // jm(defines,"FOG")). scene.fog was never set anywhere in this codebase,
    // so it defaulted ENABLED and kept dragging the ground-atmosphere
    // scattering shader into compilation -- the shader that fails MSL
    // compilation via ANGLE on this iOS/WebKit build.
    // scene.globe = undefined would also work but is NOT safe here:
    // MapModeManager and TrafficLayer dereference scene.globe (translucency,
    // ellipsoid) and would crash.
    if (scene.fog) scene.fog.enabled = false;

    // Enable celestial bodies
    // b598 — DISABLED. Root cause of "RENDERING HAS STOPPED" device-verified
    // 2026-07-22: Cesium's internal _uczm_computeGroundAtmosphere shader fails
    // MSL compilation via ANGLE on this iOS/WebKit build (engine/ANGLE bug, not
    // app code) — kills the whole render context when it fails to link. Purely
    // cosmetic loss (atmospheric glow at the globe limb); tiles/tactical/
    // live-feed rendering unaffected.
    if (scene.skyAtmosphere) scene.skyAtmosphere.show = false;
    if (scene.sun) scene.sun.show = true;
    if (scene.moon) scene.moon.show = true;
    setViewer(cesiumViewer);
    window.__viewer = cesiumViewer; // DEBUG: expose viewer for diagnostics

    // Load Google Photorealistic 3D Tiles
    try {
      const proxyHealth = await checkProxyHealth();
      if (!proxyHealth.ok) {
        showErrorOverlay(containerRef, proxyHealth.message);
        console.error("Proxy health check failed:", proxyHealth.message);
      } else {
        const isMobileClient = typeof window !== "undefined" && window.innerWidth <= 768;
        const tileset = await Cesium.Cesium3DTileset.fromUrl(GOOGLE_3D_TILES_URL, {
          shadows: Cesium.ShadowMode.DISABLED,
          // 2026-08-31 b612 CRASH + THERMAL (Brockmann on device: "phone is
          // HOT", app crashes under Cesium).
          //
          // sse 16 -> 32 on phones. Screen-space error is the tile-detail
          // knob: HALVING it roughly QUADRUPLES the tiles in view. 16 is a
          // desktop number. At 32 a 3x-DPR phone still resolves more detail
          // than the eye picks out at arm's length, and the tile count -- and
          // therefore download, decode, GPU and MEMORY -- drops hard.
          maximumScreenSpaceError: opts.maximumScreenSpaceError ?? (isMobileClient ? 32 : 16),
          // THE CRASH. cacheBytes / maximumCacheOverflowBytes were NEVER SET,
          // so the tileset ran on Cesium's desktop defaults (512MB + 512MB
          // overflow). Inside a WKWebView on an iPhone that is a jetsam kill:
          // iOS shoots the web content process and the globe "crashes the
          // app". Capping the tile cache bounds memory so the process
          // survives; tiles past the cap are evicted and re-fetched from the
          // 24h HTTP cache instead of being held in RAM forever.
          cacheBytes: isMobileClient ? 96 * 1024 * 1024 : 536870912,
          maximumCacheOverflowBytes: isMobileClient ? 32 * 1024 * 1024 : 536870912,
          // preloadWhenHidden kept fetching + decoding tiles while the globe
          // was not even on screen -- pure heat, memory and cellular data for
          // zero operator benefit. Desktop keeps it.
          preloadWhenHidden: !isMobileClient,
          preferLeaves: true,
          skipLevelOfDetail: true,
          baseScreenSpaceError: 1024,
          skipScreenSpaceErrorFactor: 16,
          skipLevels: 1,
          immediatelyLoadDesiredLevelOfDetail: false,
          loadSiblings: false,
          cullWithChildrenBounds: true
        });

        // Handle tile loading failures
        tileset.tileFailed.addEventListener(event => {
          console.warn("Tile failed to load:", event.url, event.message);
          if (event.message && isAuthError(event.message)) {
            showErrorOverlay(containerRef, API_KEY_ERROR);
          }
        });
        cesiumViewer.scene.primitives.add(tileset);
        window.__tileset = tileset;
        console.log("[CesiumProvider] Google 3D Tiles loaded successfully");
      }
    } catch (error) {
      console.error("[CesiumProvider] Failed to load Google 3D Tiles:", error);
      handleTileLoadError(containerRef, error);
    }
    setReady(true);
    console.log("[CesiumProvider] Cesium viewer initialized");
  });
  onCleanup(() => {
    const v = viewer();
    if (v && !v.isDestroyed()) {
      v.destroy();
      console.log("[CesiumProvider] Cesium viewer destroyed");
    }
  });
  const contextValue = {
    viewer,
    ready
  };
  return _$createComponent(CesiumContext.Provider, {
    value: contextValue,
    get children() {
      return [(() => {
        var _el$ = _tmpl$();
        var _ref$ = containerRef;
        typeof _ref$ === "function" ? _$use(_ref$, _el$) : containerRef = _el$;
        return _el$;
      })(), _$memo(() => props.children)];
    }
  });
}

// ============ Helper Functions ============

const PROXY_NOT_RUNNING = "Proxy server not running.\n\nStart the proxy with:\nbun run proxy";
const API_KEY_ERROR = `Google Maps API Key Error

The API key is missing or invalid.

1. Get a key from Google Cloud Console
2. Enable 'Map Tiles API'
3. Add to .env file:
   GOOGLE_MAPS_TILE_API_KEY=your_key`;
function isNetworkError(msg) {
  return msg.includes("Failed to fetch") || msg.includes("NetworkError") || msg.includes("ECONNREFUSED");
}
function isAuthError(msg) {
  return msg.includes("403") || msg.includes("401");
}
async function checkProxyHealth() {
  try {
    const response = await fetch(PROXY_ENDPOINTS.health, {
      signal: AbortSignal.timeout(5000)
    });
    return response.ok ? {
      ok: true,
      message: "Proxy is healthy"
    } : {
      ok: false,
      message: `Proxy returned status ${response.status}`
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    return {
      ok: false,
      message: isNetworkError(msg) ? PROXY_NOT_RUNNING : `Proxy health check failed: ${msg}`
    };
  }
}
function handleTileLoadError(container, error) {
  const msg = error instanceof Error ? error.message : String(error);
  if (isAuthError(msg)) {
    showErrorOverlay(container, API_KEY_ERROR);
  } else if (isNetworkError(msg)) {
    showErrorOverlay(container, PROXY_NOT_RUNNING);
  } else {
    showErrorOverlay(container, `Failed to load 3D tiles:\n${msg}`);
  }
}
function showErrorOverlay(container, message) {
  // Prevent duplicate overlays
  if (document.getElementById("globe-error-overlay")) return;
  const overlay = document.createElement("div");
  overlay.id = "globe-error-overlay";
  overlay.className = "globe-error-overlay";
  overlay.textContent = message;
  container.appendChild(overlay);
}
export default CesiumProvider;